import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '@/config/firebase';

// ================================================
// COLABORADORES PUBLIC QUERY HOOK
// Busca dados da coleção collaborators_public (somente leitura)
// ================================================

// Tipo específico para dados públicos
export interface CollaboratorPublic {
    // Identificação básica
    id: string;
    name: string;
    email: string;
    status: string;
    photoUrl?: string | null;
    phone?: string | null;

    // Localização
    city?: string | null;
    state?: string | null;
    latitude?: number | null;
    longitude?: number | null;

    // Estrutura organizacional
    positionId?: string | null;
    positionName?: string | null;
    responsibleId?: string | null;
    responsibleName?: string | null;
    departmentId?: string | null;
    departmentName?: string | null;
    clientResponsible?: string | null;

    // Características profissionais
    isRaroAcademyAlumnus?: boolean;
    resources?: any[];

    // Férias
    allocationManagerUntil?: string | null;

    // Metadados
    _metadata?: {
        lastSyncFrom?: any;
        version?: number;
        syncedFromPrivate?: boolean;
        originalUpdatedAt?: any;
    };
}

// Query keys para colaboradores públicos
export const collaboratorsPublicQueryKeys = {
    all: ['collaborators_public'] as const,
    lists: () => [...collaboratorsPublicQueryKeys.all, 'list'] as const,
    list: (filters: string) => [...collaboratorsPublicQueryKeys.lists(), { filters }] as const,
};

// Mantém um único listener Firestore ativo
const publicSnapshotManager: {
    unsub: (() => void) | null;
    listeners: number;
} = {
    unsub: null,
    listeners: 0,
};

interface PublicRealtimeQueryResult {
    data: CollaboratorPublic[];
    isLoading: boolean;
    error: Error | null;
}

function buildActiveCollaboratorsQuery() {
    return query(
        collection(db, 'collaborators_public'),
        where('status', '==', 'ACTIVE'),
        orderBy('name')
    );
}

/**
 * Hook para buscar colaboradores da coleção pública em tempo real
 * Otimizado para performance com cache e listener único
 */
export function useCollaboratorsPublicQuery(): PublicRealtimeQueryResult {
    const queryClient = useQueryClient();

    // Query inicial com cache
    const queryResult = useQuery<CollaboratorPublic[]>({
        initialData: () => (queryClient.getQueryData(collaboratorsPublicQueryKeys.all) as CollaboratorPublic[] | undefined) ?? [],
        queryKey: collaboratorsPublicQueryKeys.all,
        queryFn: async () => {
            // Se já existe no cache, retorna direto
            const cached = queryClient.getQueryData(collaboratorsPublicQueryKeys.all) as CollaboratorPublic[] | undefined;
            if (cached && cached.length) return cached;

            console.log('[public-query] Fetching from collaborators_public');
            const snap = await getDocs(buildActiveCollaboratorsQuery());
            return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CollaboratorPublic));
        },
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    // Listener em tempo-real para manter cache atualizado
    useEffect(() => {
        publicSnapshotManager.listeners += 1;

        // Cria o listener se ainda não existir
        if (!publicSnapshotManager.unsub) {
            console.log('[public-query] Creating Firestore snapshot for public collection');
            publicSnapshotManager.unsub = onSnapshot(buildActiveCollaboratorsQuery(), (snap) => {
                const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CollaboratorPublic));
                console.log('[public-query] Snapshot update from public collection', data.length);
                queryClient.setQueryData(collaboratorsPublicQueryKeys.all, data);
            });
        }

        return () => {
            publicSnapshotManager.listeners -= 1;
            if (publicSnapshotManager.listeners === 0 && publicSnapshotManager.unsub) {
                console.log('[public-query] Disposing public snapshot');
                publicSnapshotManager.unsub();
                publicSnapshotManager.unsub = null;
            }
        };
    }, [queryClient]);

    return {
        data: queryResult.data || [],
        isLoading: queryResult.isLoading,
        error: queryResult.error,
    };
}

/**
 * Hook simples para buscar colaboradores públicos (sem tempo real)
 * Use quando não precisar de atualizações automáticas
 */
export function useCollaboratorsPublicStaticQuery() {
    return useQuery<CollaboratorPublic[]>({
        queryKey: ['collaborators_public_static'],
        queryFn: async () => {
            console.log('[public-static] Fetching from collaborators_public');
            const snap = await getDocs(buildActiveCollaboratorsQuery());
            return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CollaboratorPublic));
        },
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
} 
